import React, { useEffect, useState, Fragment } from "react";
import { path, pipe, prop, sortBy, toLower } from "ramda";
import { firstError, hasError } from "common/validation";
import { isBlank, notBlank } from "common/general";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import CustomizableTextField from "./CustomizableTextField";
import { FormControl, MenuItem } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import IntlAutocomplete from "./IntlAutocomplete";
import IntlDialogActions from "./IntlDialogActions";
import IntlInputLabel from "./IntlInputLabel";
import IntlSelect from "./IntlSelect";

const ContributorDialog = ({
  contributorRoles,
  errors,
  fields,
  onBlur,
  onCancel,
  onFieldChange,
  onRoleChange,
  onSave,
  title,
  visible,
}) => {
  const { t } = useTranslation();
  const [roleOptions, setRoleOptions] = useState([]);
  useEffect(() => {
    const roles = pipe(sortBy(prop("name")))(contributorRoles);
    setRoleOptions(roles);
  }, [contributorRoles]);

  return (
    <Dialog open={visible}>
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormControl fullWidth>
              <IntlInputLabel htmlFor="contributor-type">
                {t("contributor_dialog_contributor_type")}
              </IntlInputLabel>
              <IntlSelect
                value={path(["type"], fields) || "PERSON"}
                onChange={onFieldChange(["type"])}
                inputProps={{
                  name: "contributor-type",
                  id: "contributor-type",
                }}
              >
                <MenuItem value="PERSON" key="PERSON">
                  {t("contributor_dialog_contributor_type_person")}
                </MenuItem>
                <MenuItem value="CORPORATION" key="CORPORATION">
                  {t("contributor_dialog_contributor_type_corporation")}
                </MenuItem>
              </IntlSelect>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12}>
            <IntlAutocomplete
              disableClearable
              autoSelect
              options={roleOptions}
              value={path(["role"], fields) || ""}
              getOptionLabel={role =>
                // We might move translations to stip in the future
                // Base translation on role.name for now
                // If key doesn't exist, use role.display as fallback
                notBlank(role)
                  ? t(
                      `personal_details.roles.${toLower(role.name)}`,
                      role.display
                    )
                  : ""
              }
              getOptionSelected={(opt, val) =>
                isBlank(val) || opt.name === val.name
              }
              onChange={onRoleChange}
              renderInput={params => {
                params.inputProps["aria-autocomplete"] = "none";
                params.inputProps["id"] = null;
                return (
                  <CustomizableTextField
                    params={params}
                    fieldOption="contributor_role"
                    onBlur={onBlur("role", path(["role"], fields))}
                    error={hasError(errors, "role")}
                    helperText={t(firstError(errors, "role"))}
                    name={"role-" + Math.random()}
                    defaultLabel={t("personal_details.role", "Role")}
                  />
                );
              }}
            />
          </Grid>

          {fields.type !== "CORPORATION" ? (
            <Fragment>
              <CustomizableTextField
                fieldOption="contributor_prefix"
                xs={6}
                sm={4}
                defaultLabel={t("personal_details.prefix", "Prefix (optional)")}
                value={fields.prefix || ""}
                onChange={onFieldChange(["prefix"])}
                error={hasError(errors, "prefix")}
                helperText={t(firstError(errors, "prefix"))}
                onBlur={onBlur("prefix", fields.prefix)}
                inputProps={{ maxLength: 50 }}
              />

              <Grid item xs={12} sm={12}>
                <Grid container spacing={2}>
                  <CustomizableTextField
                    fieldOption="contributor_first_name"
                    xs={12}
                    sm={4}
                    defaultLabel={t(
                      "personal_details.first_name",
                      "First Name"
                    )}
                    value={fields.firstName || ""}
                    onChange={onFieldChange(["firstName"])}
                    error={hasError(errors, "firstName")}
                    helperText={t(firstError(errors, "firstName"))}
                    onBlur={onBlur("firstName", fields.firstName)}
                    inputProps={{ maxLength: 100 }}
                  />

                  <CustomizableTextField
                    fieldOption="contributor_middle_name"
                    xs={12}
                    sm={4}
                    defaultLabel={t(
                      "personal_details.middle_name",
                      "Middle Name"
                    )}
                    value={fields.middleName || ""}
                    onChange={onFieldChange(["middleName"])}
                    error={hasError(errors, "middleName")}
                    helperText={t(firstError(errors, "middleName"))}
                    onBlur={onBlur("middleName", fields.middleName)}
                    inputProps={{ maxLength: 100 }}
                  />

                  <CustomizableTextField
                    fieldOption="contributor_last_name"
                    xs={12}
                    sm={4}
                    defaultLabel={t("personal_details.last_name", "Last Name")}
                    value={fields.lastName || ""}
                    onChange={onFieldChange(["lastName"])}
                    error={hasError(errors, "lastName")}
                    helperText={t(firstError(errors, "lastName"))}
                    onBlur={onBlur("lastName", fields.lastName)}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
              </Grid>

              <CustomizableTextField
                fieldOption="contributor_suffix"
                xs={6}
                sm={4}
                defaultLabel={t("personal_details.suffix", "Suffix (optional)")}
                value={fields.suffix || ""}
                onChange={onFieldChange(["suffix"])}
                error={hasError(errors, "suffix")}
                helperText={t(firstError(errors, "suffix"))}
                onBlur={onBlur("suffix", fields.suffix)}
                inputProps={{ maxLength: 100 }}
              />
            </Fragment>
          ) : (
            <Fragment>
              <CustomizableTextField
                fieldOption="contributor_corporation_name"
                xs={12}
                sm={12}
                defaultLabel={t(
                  "personal_details.corporation_name",
                  "Corporation Name"
                )}
                value={fields.corporationName || ""}
                onChange={onFieldChange(["corporationName"])}
                error={hasError(errors, "corporationName")}
                helperText={t(firstError(errors, "corporationName"))}
                onBlur={onBlur("corporationName", fields.corporationName)}
                inputProps={{ maxLength: 400 }}
              />
            </Fragment>
          )}

          <Grid item xs={12} sm={12}>
            <Grid container spacing={2}>
              <CustomizableTextField
                fieldOption="contributor_website"
                xs={12}
                sm={6}
                defaultLabel={t(
                  "personal_details.website_url",
                  "Website URL (optional)"
                )}
                value={fields.website || ""}
                onChange={onFieldChange(["website"])}
                error={hasError(errors, "website")}
                helperText={t(firstError(errors, "website"))}
                onBlur={onBlur("website", fields.website)}
                inputProps={{ maxLength: 2048 }}
              />

              <CustomizableTextField
                fieldOption="contributor_twitter_handle"
                xs={12}
                sm={6}
                defaultLabel={t(
                  "personal_details.twitter_handle",
                  "Twitter handle (optional)"
                )}
                value={fields.twitterHandle || ""}
                onChange={onFieldChange(["twitterHandle"])}
                error={hasError(errors, "twitterHandle")}
                helperText={t(firstError(errors, "twitterHandle"))}
                onBlur={onBlur("twitterHandle", fields.twitterHandle)}
                inputProps={{ maxLength: 30 }}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <IntlDialogActions>
        <Button onClick={onCancel}> {t("app_cancel")}</Button>
        <Button variant="contained" color="secondary" onClick={onSave}>
          {t("app_save")}
        </Button>
      </IntlDialogActions>
    </Dialog>
  );
};

export default ContributorDialog;
